/**
 * Shared postcode helpers for store-availability sorting and km distances.
 * User postcode comes from the shipping calculator (localStorage d1_postcode).
 */
(function (root) {
  'use strict';
  if (root.D1PickupProximity) return;

  // Same AU ranges as delivery-estimate.js, with a state for proximity.
  var ZONES = [
    { min: 3000, max: 3207, state: 'VIC' },
    { min: 3208, max: 3999, state: 'VIC' },
    { min: 8000, max: 8999, state: 'VIC' },
    { min: 2600, max: 2620, state: 'ACT' },
    { min: 2000, max: 2249, state: 'NSW' },
    { min: 2250, max: 2599, state: 'NSW' },
    { min: 2621, max: 2999, state: 'NSW' },
    { min: 4000, max: 4399, state: 'QLD' },
    { min: 4400, max: 4999, state: 'QLD' },
    { min: 9000, max: 9999, state: 'QLD' },
    { min: 5000, max: 5199, state: 'SA' },
    { min: 5200, max: 5999, state: 'SA' },
    { min: 6000, max: 6199, state: 'WA' },
    { min: 6200, max: 6999, state: 'WA' },
    { min: 7000, max: 7999, state: 'TAS' },
    { min: 800, max: 999, state: 'NT' },
  ];

  function normalizePostcode(value) {
    if (!value) return '';
    return String(value).replace(/\D/g, '').slice(0, 4);
  }

  function stateForPostcode(postcode) {
    var pc = parseInt(normalizePostcode(postcode), 10);
    if (isNaN(pc)) return '';
    for (var i = 0; i < ZONES.length; i++) {
      if (pc >= ZONES[i].min && pc <= ZONES[i].max) return ZONES[i].state;
    }
    return '';
  }

  function readUserPostcode() {
    var fromStorage = '';
    try {
      fromStorage = normalizePostcode(root.localStorage && root.localStorage.getItem('d1_postcode'));
    } catch (e) {
      fromStorage = '';
    }
    if (/^\d{4}$/.test(fromStorage)) return fromStorage;

    var doc = root.document;
    if (!doc || !doc.querySelector) return '';

    var resolved = doc.querySelector('[data-d1-resolved-postcode]');
    var fromResolved = normalizePostcode(
      resolved && (resolved.getAttribute('data-d1-resolved-postcode') || resolved.getAttribute('value'))
    );
    if (/^\d{4}$/.test(fromResolved)) return fromResolved;

    var customer = doc.querySelector('[data-d1-customer-zip]');
    var fromAccount = normalizePostcode(
      customer && (customer.value || customer.getAttribute('value') || customer.getAttribute('data-d1-customer-zip'))
    );
    if (/^\d{4}$/.test(fromAccount)) return fromAccount;

    var zone = doc.querySelector('.d1-delivery__zone');
    var zoneText = zone && zone.textContent ? zone.textContent : '';
    var zoneMatch = zoneText.match(/\b(\d{4})\b/);
    return zoneMatch ? zoneMatch[1] : '';
  }

  function filterKeyForPostcode(postcode) {
    var state = stateForPostcode(postcode);
    if (state === 'VIC' || state === 'NSW' || state === 'QLD' || state === 'WA') return state;
    return 'ALL';
  }

  // Same-state stores first, then smaller postcode gap. Cross-state +10000.
  function proximityScore(storePostcode, storeState, userPostcode) {
    var user = normalizePostcode(userPostcode);
    var store = normalizePostcode(storePostcode);
    if (!user || !store) return 50000;
    var userN = parseInt(user, 10);
    var storeN = parseInt(store, 10);
    if (isNaN(userN) || isNaN(storeN)) return 50000;
    var userState = stateForPostcode(user);
    var st = String(storeState || '').toUpperCase();
    var penalty = userState && st && userState !== st ? 10000 : 0;
    return penalty + Math.abs(storeN - userN);
  }

  function compareStores(a, b, userPostcode) {
    var stockDelta = (a.stockRank || 0) - (b.stockRank || 0);
    if (stockDelta !== 0) return stockDelta;
    var aKm = a.distanceKm;
    var bKm = b.distanceKm;
    var aHas = typeof aKm === 'number' && isFinite(aKm);
    var bHas = typeof bKm === 'number' && isFinite(bKm);
    if (aHas && bHas) return aKm - bKm;
    if (aHas) return -1;
    if (bHas) return 1;
    if (!userPostcode) return 0;
    return (
      proximityScore(a.postcode, a.state, userPostcode) -
      proximityScore(b.postcode, b.state, userPostcode)
    );
  }

  // Fallback when Shopify location.latitude is nil. Keys are lowercase aliases.
  var STORE_COORDS = {
    brunswick: { lat: -37.7663, lng: 144.9575 },
    chadstone: { lat: -37.886624, lng: 145.082399 },
    'melbourne central': { lat: -37.810682, lng: 144.963007 },
    'fountain gate': { lat: -38.0174, lng: 145.3043 },
    'westfield fountain gate': { lat: -38.0174, lng: 145.3043 },
    bondi: { lat: -33.8914, lng: 151.2509 },
    'bondi junction': { lat: -33.8914, lng: 151.2509 },
    macquarie: { lat: -33.7768, lng: 151.1194 },
    'pitt street': { lat: -33.8704, lng: 151.2073 },
    'pitt street mall': { lat: -33.8704, lng: 151.2073 },
    'westfield sydney': { lat: -33.8704, lng: 151.2073 },
    'world square': { lat: -33.8772, lng: 151.2069 },
    'world square sydney': { lat: -33.8772, lng: 151.2069 },
    carindale: { lat: -27.5023, lng: 153.1014 },
    'westfield carindale': { lat: -27.5023, lng: 153.1014 },
    chermside: { lat: -27.3847, lng: 153.0319 },
    'westfield chermside': { lat: -27.3847, lng: 153.0319 },
    carousel: { lat: -32.0177, lng: 115.9375 },
    'westfield carousel': { lat: -32.0177, lng: 115.9375 },
    chatswood: { lat: -33.7968, lng: 151.1836 },
  };

  var STORE_COORD_KEYS = Object.keys(STORE_COORDS).sort(function (a, b) {
    return b.length - a.length;
  });

  function parseCoord(value) {
    if (value == null || value === '') return NaN;
    var n = parseFloat(String(value), 10);
    return isFinite(n) ? n : NaN;
  }

  function normalizeStoreName(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/^\s+|\s+$/g, '');
  }

  function coordsForStoreName(name) {
    var n = normalizeStoreName(name);
    if (!n) return null;
    if (STORE_COORDS[n]) return STORE_COORDS[n];
    for (var i = 0; i < STORE_COORD_KEYS.length; i++) {
      var key = STORE_COORD_KEYS[i];
      if (n.indexOf(key) !== -1) return STORE_COORDS[key];
    }
    return null;
  }

  function coordsFromCard(card) {
    if (!card) return null;
    var lat = parseCoord(card.getAttribute('data-lat'));
    var lng = parseCoord(card.getAttribute('data-lng'));
    if (isFinite(lat) && isFinite(lng)) return { lat: lat, lng: lng };
    var titleEl = card.querySelector('.pickup-availability-card__title');
    return coordsForStoreName(titleEl ? titleEl.textContent : '');
  }

  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatKm(km) {
    if (typeof km !== 'number' || !isFinite(km) || km < 0) return '';
    if (km < 1) return '< 1 km';
    if (km < 10) {
      var one = Math.round(km * 10) / 10;
      return (one % 1 === 0 ? String(Math.round(one)) : String(one)) + ' km';
    }
    return Math.round(km) + ' km';
  }

  var GEO_MEM = {};
  var GEO_STORAGE_KEY = 'd1_postcode_ll';
  var originInflight = {};

  function readGeoCache(postcode) {
    if (GEO_MEM[postcode]) return GEO_MEM[postcode];
    try {
      var raw = root.localStorage && root.localStorage.getItem(GEO_STORAGE_KEY);
      var map = raw ? JSON.parse(raw) : null;
      var hit = map && map[postcode];
      if (hit && isFinite(hit.lat) && isFinite(hit.lng)) {
        GEO_MEM[postcode] = hit;
        return hit;
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function writeGeoCache(postcode, origin) {
    GEO_MEM[postcode] = origin;
    try {
      var map = {};
      var raw = root.localStorage && root.localStorage.getItem(GEO_STORAGE_KEY);
      if (raw) map = JSON.parse(raw) || {};
      map[postcode] = origin;
      root.localStorage.setItem(GEO_STORAGE_KEY, JSON.stringify(map));
    } catch (e) {
      /* ignore */
    }
  }

  function originFromZippopotam(data) {
    var places = data && data.places;
    if (!places || !places.length) return null;
    var lat = 0;
    var lng = 0;
    var n = 0;
    for (var i = 0; i < places.length; i++) {
      var plat = parseCoord(places[i].latitude);
      var plng = parseCoord(places[i].longitude);
      if (!isFinite(plat) || !isFinite(plng)) continue;
      lat += plat;
      lng += plng;
      n += 1;
    }
    if (!n) return null;
    return { lat: lat / n, lng: lng / n };
  }

  function resolvePostcodeOrigin(postcode) {
    var pc = normalizePostcode(postcode);
    if (!/^\d{4}$/.test(pc)) return Promise.resolve(null);
    var cached = readGeoCache(pc);
    if (cached) return Promise.resolve(cached);
    if (originInflight[pc]) return originInflight[pc];
    if (typeof root.fetch !== 'function') return Promise.resolve(null);

    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = root.setTimeout(function () {
      if (ctrl) ctrl.abort();
    }, 4000);

    originInflight[pc] = root
      .fetch('https://api.zippopotam.us/au/' + pc, ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        var origin = originFromZippopotam(data);
        if (origin) writeGeoCache(pc, origin);
        return origin;
      })
      .catch(function () {
        return null;
      })
      .then(function (origin) {
        root.clearTimeout(timer);
        delete originInflight[pc];
        return origin;
      });

    return originInflight[pc];
  }

  function paintDistance(card, km, drawer) {
    var el = card.querySelector('[data-d1-pickup-km]');
    if (!el) return;
    if (typeof km !== 'number' || !isFinite(km)) {
      card.removeAttribute('data-distance-km');
      el.textContent = '';
      el.hidden = true;
      return;
    }
    card.setAttribute('data-distance-km', String(Math.round(km * 10) / 10));
    var label = formatKm(km);
    var template = (drawer && drawer.getAttribute('data-d1-pickup-km-template')) || '';
    el.textContent = template.indexOf('__KM__') !== -1 ? template.split('__KM__').join(label) : label;
    el.hidden = false;
  }

  function enrichStoreDistances(drawer, postcode) {
    if (!drawer) return Promise.resolve(false);
    return resolvePostcodeOrigin(postcode).then(function (origin) {
      var cards = drawer.querySelectorAll('.pickup-availability-card');
      if (!origin || !cards.length) {
        Array.prototype.forEach.call(cards, function (card) {
          paintDistance(card, NaN, drawer);
        });
        return false;
      }
      var painted = false;
      Array.prototype.forEach.call(cards, function (card) {
        var store = coordsFromCard(card);
        if (!store) {
          paintDistance(card, NaN, drawer);
          return;
        }
        paintDistance(card, haversineKm(origin.lat, origin.lng, store.lat, store.lng), drawer);
        painted = true;
      });
      return painted;
    });
  }

  root.D1PickupProximity = {
    normalizePostcode: normalizePostcode,
    stateForPostcode: stateForPostcode,
    readUserPostcode: readUserPostcode,
    filterKeyForPostcode: filterKeyForPostcode,
    proximityScore: proximityScore,
    compareStores: compareStores,
    haversineKm: haversineKm,
    formatKm: formatKm,
    coordsForStoreName: coordsForStoreName,
    originFromZippopotam: originFromZippopotam,
    enrichStoreDistances: enrichStoreDistances,
  };
})(typeof window !== 'undefined' ? window : globalThis);
